import { ScoreBadge } from "@aircall/blocks";
import { Tooltip, TooltipContent, TooltipTrigger } from "@aircall/ds";
import { type ReactNode } from "react";

type PercentBadgeProps = {
  // Null is "Not applicable"
  value: number | null;
  /**
   * @deprecated No-op. Kept for API compatibility with existing call sites.
   * The `@aircall/blocks` ScoreBadge owns its own border styling and no longer
   * exposes a borderless variant.
   */
  borderless?: boolean;
  // When set, the badge is wrapped in a hover tooltip showing this content.
  tooltip?: ReactNode;
};

/**
 * Thin adapter over the `@aircall/blocks` `ScoreBadge`. Renders a percentage
 * pill whose tone is derived from the value using ScoreBadge's default
 * thresholds. `kind="percent"` formats the value as a locale-aware percentage
 * (e.g. "78%").
 *
 * Behaviour differences versus the previous local implementation:
 * - Percentage formatting changed from "78.0 %" to "78%" (trailing ".0" and the
 *   space before the suffix are dropped by the shared formatter).
 * - Finite out-of-range values are no longer clamped (150 -> "150%",
 *   -10 -> "-10%"); only null/NaN/Infinity render the muted "--".
 */
export function PercentBadge({ value, tooltip }: PercentBadgeProps) {
  const badge = (
    <ScoreBadge value={value} kind="percent" data-test={`percent-badge-${value}`} />
  );

  if (tooltip == null) {
    return badge;
  }

  return (
    <Tooltip>
      {/* ScoreBadge spreads its props onto the underlying DS Badge, so it can
          be the trigger directly (no wrapper node) via the DS `render` pattern. */}
      <TooltipTrigger delay={0} closeDelay={0} render={badge} />
      <TooltipContent side="top" align="center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
